import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';
import { Header } from '../components/Header';
import { ToolCard } from '../components/ToolCard';
import { DonateModal } from '../components/DonateModal';
import { useFavoritesContext } from '../contexts/FavoritesContext';
import { useTools } from '../hooks/useTools';
import { useAuth } from '../hooks/useAuth';
export function FavoritesPage() {
  const {
    favorites,
    loading: favoritesLoading
  } = useFavoritesContext();
  const {
    tools,
    loading: toolsLoading
  } = useTools();
  const {
    user,
    signInWithGoogle
  } = useAuth();
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const loading = favoritesLoading || toolsLoading;
  const currentYear = new Date().getFullYear();
  // Filter tools to only show favorites
  const favoriteTools = tools.filter(tool => favorites.includes(tool.id));
  return <div className="min-h-screen bg-black flex flex-col" data-id="element-1070">
      <Header data-id="element-1071" />

      <main className="flex-1 pt-[156px] pb-16 px-4" data-id="element-1072">
        <div className="max-w-7xl mx-auto" data-id="element-1073">
          {/* Page Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-12" data-id="element-1074">
            <h1 className="text-3xl md:text-6xl font-black text-white mb-4 font-hero [line-height:2.4rem] md:[line-height:4.2rem]" data-id="element-1075">
              Saved Tools
            </h1>

            <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-1076">
              {user ? <>Your favorite AI tools, synced across all devices</> : <>Your favorite AI tools.</>}
            </p>
          </motion.div>

          {/* Loading State */}
          {loading && <div className="text-center py-20" data-id="element-1077">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent" data-id="element-1078"></div>
              <p className="text-gray-400 mt-4" data-id="element-1079">Loading your favorites...</p>
            </div>}

          {/* Empty State */}
          {!loading && favoriteTools.length === 0 && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-20" data-id="element-1080">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-1081">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" className="text-gray-500" data-id="element-1082">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1083" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3" data-id="element-1084">
                No favorites yet
              </h2>
              <p className="text-gray-400 mb-8 max-w-[80%] mx-auto" data-id="element-1085">
                Start exploring and click the heart icon on any tool to save it
                here
              </p>
              <a href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1086">
                <Sparkles size={18} data-id="element-1087" />
                Explore Tools
              </a>
            </motion.div>}

          {/* Favorites Grid */}
          {!loading && favoriteTools.length > 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-1088">
              {favoriteTools.map((tool, index) => <motion.div key={tool.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} data-id="element-1089">
                  <ToolCard tool={tool} data-id="element-1090" />
                </motion.div>)}
            </motion.div>}

          {/* Stats */}
          {!loading && favoriteTools.length > 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="mt-12 text-center" data-id="element-1091">
              <p className="text-sm text-gray-500" data-id="element-1092">
                {favoriteTools.length}{' '}
                {favoriteTools.length === 1 ? 'tool' : 'tools'} saved
                {user && ' • Synced to your account'}
              </p>
            </motion.div>}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 bg-black/80 backdrop-blur-lg mt-auto" data-id="element-1093">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-1094">
          <div className="flex items-center gap-2" data-id="element-1095">
            <span style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 300,
            fontSize: '1.125rem',
            lineHeight: '1.75rem',
            letterSpacing: '.15em'
          }} className="text-white" data-id="element-1096">
              FAIND
            </span>
          </div>
          <div className="text-gray-500 text-sm" data-id="element-1097">
            © {currentYear} Faind. All rights reserved.
          </div>
          <div className="flex items-center gap-6" data-id="element-1098">
            <a href="https://x.com/faindyourapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Follow us on X" data-id="element-1099">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" data-id="element-1100">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-1101" />
              </svg>
            </a>
            <button onClick={() => setIsDonateModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-pink-500/30 text-gray-400 hover:text-pink-400 transition-all text-sm font-medium" data-id="element-1102">
              <Heart size={14} data-id="element-1103" />
              Donate
            </button>
          </div>
        </div>
      </footer>

      <DonateModal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} data-id="element-1104" />
    </div>;
}